import { useEffect, useRef, useState } from 'react';
import Reveal from '@/components/base/Reveal';
import { useInView } from '@/hooks/useInView';

interface StatItem {
  value: number;
  suffix: string;
  label: string;
}

const stats: StatItem[] = [
  { value: 999, suffix: '+', label: 'Weekly Listeners' },
  { value: 2999, suffix: '+', label: 'Songs Played Daily' },
  { value: 11, suffix: '+', label: 'Volunteers' },
  { value: 8, suffix: 'yrs', label: 'Market Experience' },
];

function CountUp({ target, suffix, duration = 1800 }: { target: number; suffix: string; duration?: number }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) setStarted(true); },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const isDecimal = target % 1 !== 0;
    const steps = 60;
    const interval = duration / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * target;
      setCount(isDecimal ? Math.round(current * 10) / 10 : Math.floor(current));
      if (step >= steps) { setCount(target); clearInterval(timer); }
    }, interval);
    return () => clearInterval(timer);
  }, [started, target, duration]);

  const isDecimal = target % 1 !== 0;
  return <span ref={ref}>{isDecimal ? count.toFixed(1) : count}{suffix}</span>;
}

export default function StatsSection() {
  const [sectionRef, sectionInView] = useInView<HTMLElement>({ threshold: 0.1 });

  return (
    <section ref={sectionRef} className="relative py-20 px-6 md:px-12 lg:px-16 bg-black border-t border-white/5">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
          {stats.map((stat, i) => (
            <Reveal key={stat.label} variant="scaleUp" delay={i * 120} duration={700} threshold={0.2}>
              <div className="bg-black px-8 py-12 lg:py-16 group hover:bg-white/[0.02] transition-colors duration-300 h-full">
                <div
                  className="text-5xl md:text-6xl lg:text-7xl font-light font-['Instrument_Serif'] text-white mb-3"
                  style={{ letterSpacing: '-0.02em' }}
                >
                  {sectionInView && <CountUp target={stat.value} suffix={stat.suffix} />}
                  {!sectionInView && <span>0{stat.suffix}</span>}
                </div>
                <div className="text-xs text-white/55 tracking-wide">{stat.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
